import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/internal/operators/map';

import { DataDictionaryApi } from '../../../api/data-dictionary-api';
import { util } from '../../../classes/util';
import { InputConverter } from '../../../inputConverter';
import { FilterService } from '../../../services/filter.service';
import { LayerService } from '../../../services/layer.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { ILayer, ILayerColumn, IMapcoreFeatureLayer, IMapcoreSubLayer, IMetaTableColumn, IOperatorOption, IQuery, isQueryableMapCoreLayer, ITable, IWorkingQuery, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

const shortWait = 10;

/** Basic Filter Component - Allows editing of a layer filter */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-basic-filter-ng',
    styleUrls: ['../../sharedStyles/base.scss', './basic-filter.component.scss'],
    templateUrl: './basic-filter.component.html'
})
export class BasicFilterComponent extends BaseComponent {
    layerQuery: IQuery;
    operatorOptions: IOperatorOption[];
    openChangeListenerAdded: boolean = false;
    parentTabName: string;
    showSQL: boolean;
    _summaryItemsString = '';
    userSubscriber: any;
    userId = null;
    table: ITable;
    globalFilterOnPrevious = false;
    refiltered: boolean = false;
    previousOperator: string = '';
    previousValue: string[] = [];

    @Input() set summaryItemsString(inputSummary: string) {
        this._summaryItemsString = inputSummary;
    }
    get summaryItemsString() {
        return this._summaryItemsString;
    }

    _layerId: number;
    get layerId() { return this._layerId; }
    @Input()
    set layerId(layerId: number) {
        this.setLayer(layerId);
    }

    @Input()
    esriLayer: MapCoreLayer;

    _externalLayerId: string;
    get externalLayerId() { return this._externalLayerId; }
    @Input() set externalLayerId(extLayerId: string) {
        this._externalLayerId = extLayerId;
        this.setLayer(-1);
    }

    @Input() layerColumns: ILayerColumn[] = [];
    layerColumnsDisplay: ILayerColumn[] = [];
    @Input() sublayers: IMapcoreSubLayer[];
    @Input()
    @InputConverter()
    updateLayerOnApply: boolean = true;

    _query: IQuery;
    get query() {
        return this._query;
    }
    @Input() set query(inputQuery: IQuery) {
        this._query = inputQuery;
        this.populateWorkingQueryFromQuery();
        this.refreshSubqueries();
    }

    _extraFilters: IQuery[];
    get extraFilters() {
        return this._extraFilters;
    }
    @Input() set extraFilters(extraFilters: IQuery[]) {
        this._extraFilters = extraFilters;
    }

    onFieldChange(field, subquery, subqueryIndex) {
        subquery.field = field;
        this.selectedFilterColumn(subquery, subqueryIndex);
    }

    async selectedFilterColumn(subquery: IWorkingQuery, subqueryIndex) {
        const tempValue = subquery.value;
        subquery.multiValue = null;
        subquery.value = null;
        subquery.dateFormat = null;
        subquery.inputType = null;
        this.changeDetectorRef.detectChanges();
        if (this.layerId > 0 && !(this.esriLayer && this.esriLayer.mapcoreProps && this.esriLayer.mapcoreProps.isExternal)) {
            subquery = await this.filterService.populateDistinctValues(this.layerId, subquery, this.layerQuery, this.workingQuery, this.layerColumns, subqueryIndex, null, this.isPETL);
        } else {
            subquery = await this.filterService.populateExternalDistinctValues(this.externalLayerId, subquery, this.workingQuery, this.workingSql, subqueryIndex);
            if (this.refiltered && subquery.distinctValues && subquery.distinctValues.some(dv => dv.value === tempValue)) {
                subquery.value = tempValue;
            }
        }

        this.refreshSubqueries();
        this.filterService.detectDateFormat(subquery, this.layerColumns);
    }

    _layerRef: ILayer;
    @Input()
    get layerRef() {
        return this._layerRef;
    }
    set layerRef(value: ILayer) {
        this._layerRef = value;
        if (value) {
            this.onlayerdata.emit(this._layerRef);
        }
    }

    _workingQuery: IWorkingQuery;
    @Input() set workingQuery(inputQuery: IWorkingQuery) {
        this._workingQuery = inputQuery;
        this.addBottomSubqueryIfNeeded();
    }
    get workingQuery() {
        return this._workingQuery;
    }

    @Input() async updateWorkingSql(summaryString) {
        this.summaryItemsString = summaryString;
        await this.populateWorkingSql();
        this.changeDetectorRef.detectChanges();
    }
    @Input() updateQuery = (query) => {
        this._outsideUpdate = true;
        this._query = query;
    }

    private _outsideUpdate = false;

    @Input() workingSql: string;

    @Output() public readonly ondone = new EventEmitter<{ layerId: number; sql: string; query: IQuery }>();
    @Output() public readonly onlayerdata = new EventEmitter<ILayer>();
    @Output() public readonly workingQueryChange = new EventEmitter<{ workingQuery: IWorkingQuery; layerColumns: ILayerColumn[] }>();
    @Output() public readonly onapply = new EventEmitter<{ id?: string; esriLayer?: any; data?: any }>();

    get isPETL() {
        return this.esriLayer ? this.esriLayer.mapcoreProps.isPETL : null;
    }

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        public toSqlStringService: ToSqlStringService,
        private readonly filterService: FilterService,
        private readonly layerService: LayerService,
        private readonly dataDictionaryApi: DataDictionaryApi) {
        super(_vcr, true);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('onlayerdata', this.onlayerdata);
        this.registerEvent('workingQueryChange', this.workingQueryChange);
        this.registerEvent('onapply', this.onapply);
        this.registerWithMcScope();
        this.setOperatorOptions();
    }

    get andOr(): string {
        return this.workingQuery.operator === 'OR' ? 'or' : 'and';
    }

    get showFilter() {
        return this.layerColumns.length > 0;
    }

    set workingSubqueries(subqueries: IWorkingQuery[]) {
        if (this.workingQuery) {
            let deepSubqueries: IWorkingQuery[];
            if (this.workingQuery.subqueries) {
                deepSubqueries = this.workingQuery.subqueries.filter(query => query.subqueries && query.subqueries.length);
            }
            this.workingQuery.subqueries = subqueries;
            if (deepSubqueries) {
                this.workingQuery.subqueries.push(...deepSubqueries);
            }
        }
    }
    get workingSubqueries(): IWorkingQuery[] {
        if (this.workingQuery) {
            return this.workingQuery.subqueries.filter(query => !(query.subqueries && query.subqueries.length));
        }
        return null;
    }

    showMultiple(operator: string) {
        return this.filterService.showMultiple(operator);
    }

    addBottomSubqueryIfNeeded() {
        if (this.workingSubqueries) {
            let addWorkingSubquery = this.workingSubqueries.length === 0;
            if (!addWorkingSubquery) {
                const lastSubquery = this.workingSubqueries[this.workingSubqueries.length - 1];
                addWorkingSubquery = this.toSqlStringService.queryConditionIsValid(lastSubquery);
            }
            if (addWorkingSubquery) {
                const addQuery = { field: '', operator: '=', value: null, filterDistinctOnPrevious: this.globalFilterOnPrevious ? true : false } as IWorkingQuery;
                addQuery.operatorOptions = this.filterService.getOperatorOptionsForSubquery(addQuery, this.layerColumns);
                this.workingQuery.subqueries.push(addQuery);
            }
        }
    }

    @Input() finalizeQuery = () => {
        this.query = this.filterService.getFinalizedQuery(this.workingQuery);
    }

    applyFilter() {
        this.finalizeQuery();

        if (this.layerId > 0) {
            this.filterService.saveLayerUsage(this.layerId, this.query);
            const data = { layerId: this.layerId, sql: this.workingSql, query: this.query, parentQuery: this.layerQuery };
            this.ondone.emit(data);

            if (this.updateLayerOnApply) {
                this.onapply.emit({ id: this.layerId.toString(), data });

                if (this.esriLayer && data && isQueryableMapCoreLayer(this.esriLayer)) {
                    this.esriLayer.mapcoreProps.filter = data.sql;
                    this.esriLayer.mapcoreProps.query = data.query;

                    this.layerService.updateLayerFilter(this.esriLayer, data.query);
                    this.onapply.emit({ esriLayer: this.esriLayer });
                } else {
                    this.onapply.emit(null);
                }
            }

            this.filterService.onApplyFilter(this.esriLayer);
        } else if (this.externalLayerId !== undefined) {
            //external layers apply
            const esriLayer = this.layerService.getEsriLayerByUniqueId(this.externalLayerId) as IMapcoreFeatureLayer;
            esriLayer.mapcoreProps.filter = this.workingSql;
            esriLayer.mapcoreProps.query = this.query;
            esriLayer.definitionExpression = this.workingSql;
            this.ondone.emit();
        }
        this.resetBasicFilter();
    }

    cancel() {
        this.resetBasicFilter();
        this.ondone.emit(null);
    }

    emitWorkingQuery() {
        if (!this._outsideUpdate) {
            this.workingQueryChange.emit({ workingQuery: this.workingQuery, layerColumns: this.layerColumns });
        }
        this._outsideUpdate = false;
    }

    populateWorkingQueryFromQuery() {
        if (this.query) {
            this.workingQuery = this.filterService.populateWorkingQueryFromQuery(this.query, this.layerColumns);
            this.addBottomSubqueryIfNeeded();
        }
    }

    async populateWorkingSql() {
        if (this.layerId > 0) {
            if (this.table) {
                this.workingSql = await this.filterService.populateWorkingSql(this.layerQuery, this.workingQuery, this.table, this.layerColumns);
            }
        } else if (this.workingQuery) {
            this.workingSql = await this.toSqlStringService.buildExternalWorkingSql(this.workingQuery, this.layerColumns);
        }
    }

    refreshSubqueries = util.debounce(async () => {
        await this.refreshSubqueriesWork();
    }, shortWait);
    private async refreshSubqueriesWork() {
        this.filterService.clearValuesIfNeeded(this.workingSubqueries);
        this.workingSubqueries = this.filterService.populateWorkingSubqueryDisplayProperties(this.workingSubqueries, this.layerColumns);
        if (this.workingSubqueries && this.workingSubqueries.length) {
            const populatePromises: Promise<any>[] = [];
            this.workingSubqueries.forEach((sq, i) => {
                if (!sq.distinctValues) {
                    populatePromises.push(this.filterService.populateDistinctValues(this.layerId, sq, this.layerQuery, this.workingQuery, this.layerColumns, i, null, this.isPETL));
                }
            });
            await Promise.all(populatePromises);
        }
        this.addBottomSubqueryIfNeeded();
        this.emitWorkingQuery();
        this.changeDetectorRef.detectChanges();
    }

    async populateLayerData() {
        if (this._layerId && this._layerId > 0) {
            const layers = await this.filterService.getLayerData(this.layerId);

            if (layers.length > 0) {
                if (this.esriLayer && this.esriLayer.mapcoreProps) {
                    this.layerRef = this.esriLayer.mapcoreProps;
                    this.layerQuery = this.layerRef.parentQuery;
                } else {
                    this.layerRef = layers[0];
                    this.layerQuery = this.layerRef.queryJSON ? JSON.parse(this.layerRef.queryJSON) : this.filterService.getEmptyQuery();
                }

                this.replacePlanYearNoQuery();
                this.replaceParentQuery();

                const tables = this.layerRef.tables;
                let dataDictionaryColumns: IMetaTableColumn[];

                if (tables) {
                    this.table = tables.sort((a, b) => a.geometryType.localeCompare(b.geometryType))[0];
                    if (this.table && this.table.metaTable) {
                        dataDictionaryColumns = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(this.table.metaTable.id);
                    }
                }

                if (!this.layerColumns.length && this.layerRef && this.layerRef.columns) {
                    this.layerColumns.push(...this.layerRef.columns);
                }

                for (const layerColumn of this.layerColumns) {
                    layerColumn.name = layerColumn.name.toLowerCase();

                    if (!layerColumn.alias) {
                        layerColumn.alias = layerColumn.name.toUpperCase();
                    }

                    if (dataDictionaryColumns) {
                        const matchingColumn = dataDictionaryColumns.find(c => c.columnName.toLowerCase().trim() === layerColumn.name.toLowerCase().trim());
                        if (matchingColumn && matchingColumn.domainValues && matchingColumn.domainValues.length) {
                            layerColumn.domainValues = matchingColumn.domainValues;
                        }
                    }
                }

                this.layerColumnsDisplay = this.layerColumns;
                this.sortLayerColumnsDisplay();
                return this.layerColumns;
            }
        } else {
            await this.populateExternalLayerData();
        }
        this.sortLayerColumnsDisplay();
        return [];
    }

    private async populateExternalLayerData() {
        const esriLayer = this.layerService.getEsriLayerByUniqueId(this.externalLayerId) as IMapcoreFeatureLayer;
        this.layerColumns.length = 0;
        this.layerColumns = [...this.esriLayer.mapcoreProps.columns];

        if (esriLayer.mapcoreProps.query) {
            this.refiltered = true;
            this.workingSubqueries = this.workingQuery.subqueries;
            if (this.workingSubqueries.length !== 0) {
                //onload if this filter already has subqueries, "select" each to load its distinct values
                let i = 0;
                for (const sq of this.workingSubqueries) {
                    if (!sq.value && !sq.field) {
                        break;
                    }
                    await this.selectedFilterColumn(sq, i);
                    i++;
                }
            }
        }
        this.layerColumnsDisplay = [...this.layerColumns];
    }

    remove(i: number) {
        this.workingSubqueries = this.workingSubqueries.filter((_sq, index) => index !== i);
        this.refreshSubqueries();
    }

    setAll() {
        this.workingQuery.operator = 'AND';
        this.refreshSubqueries();
    }

    setAny() {
        this.workingQuery.operator = 'OR';
        this.refreshSubqueries();
    }

    async setLayer(layerId: number) {
        this._layerId = layerId;
        this.workingSql = '';
        this.layerRef = undefined;
        this.table = null;
        this.layerColumns.length = 0;
        this.layerColumnsDisplay.length = 0;
        await this.populateLayerData();
        if (!this.refiltered) {
            this.populateWorkingQueryFromQuery();
            this.refreshSubqueries();
            this.refiltered = false;
        } else {
            this.workingSubqueries = this.filterService.populateWorkingSubqueryDisplayProperties(this.workingSubqueries, this.layerColumns);
            this.changeDetectorRef.detectChanges();
        }
    }

    setOperatorOptions() {
        this.operatorOptions = this.toSqlStringService.getOperatorOptions();
    }

    async setFilterOnPrevious(subquery?: IWorkingQuery, singleSubQueryIndex?: number) {
        if (subquery) {
            subquery.previousFilterDistinctOnPreviousValue = subquery.filterDistinctOnPrevious;
            subquery.filterDistinctOnPrevious = subquery.filterDistinctOnPrevious ? false : true;
            if (this.layerId > 0) {
                await this.filterService.populateDistinctValues(this.layerId, subquery, this.layerQuery, this.workingQuery, this.layerColumns, singleSubQueryIndex, null, this.isPETL);
            } else {
                await this.filterService.populateExternalDistinctValues(this.externalLayerId, subquery, this.workingQuery, this.workingSql, singleSubQueryIndex);
                if (!subquery.distinctValues.some(dv => dv.value === subquery.value)) {
                    subquery.value = '';
                    this.refreshSubqueries();
                }
            }
        } else {
            // Looping through all subqueries to filter by previous in turn. Does not apply to first subquery
            this.globalFilterOnPrevious = this.globalFilterOnPrevious ? false : true;
            this.workingQuery.subqueries.forEach(async (workingSubquery: IWorkingQuery, i) => {
                workingSubquery.previousFilterDistinctOnPreviousValue = workingSubquery.filterDistinctOnPrevious;
                workingSubquery.filterDistinctOnPrevious = this.globalFilterOnPrevious ? true : false;
                if (i > 0) {
                    const sq = this.workingQuery.subqueries[i];
                    if (this.layerId > 0) {
                        await this.filterService.populateDistinctValues(this.layerId, sq, this.layerQuery, this.workingQuery, this.layerColumns, i, null, this.isPETL);
                    } else {
                        if (sq) {
                            await this.filterService.populateExternalDistinctValues(this.externalLayerId, sq, this.workingQuery, this.workingSql, i);
                            if (sq.distinctValues && sq.value && !sq.distinctValues.some(dv => dv.value === sq.value)) {
                                sq.value = '';
                                this.refreshSubqueries();
                            }
                        }
                    }

                }
            });
        }
        this.changeDetectorRef.detectChanges();
    }

    toggleShowSql() {
        this.showSQL = !this.showSQL;
    }

    replaceParentQuery() {
        //Replace the parent query from the extra filter if there are any cyfy there.
        if (this.extraFilters && this.extraFilters.length > 0 && this.extraFilters[0].field === 'MAINT_FY') {
            const layerFieldName = 'CALENDAR_YEAR', field = 'cy_plan_year_no', fieldToReplace = 'fy_plan_year_no', extraFilter = this.extraFilters[0];
            this.replaceFields(extraFilter, layerFieldName, field, fieldToReplace);
        } else if (this.extraFilters && this.extraFilters.length > 0 && this.extraFilters[0].field === 'CALENDAR_YEAR') {
            const layerFieldName = 'MAINT_FY', field = 'fy_plan_year_no', fieldToReplace = 'cy_plan_year_no', extraFilter = this.extraFilters[0];
            this.replaceFields(extraFilter, layerFieldName, field, fieldToReplace);
        }
    }

    replacePlanYearNoQuery() {
        const fieldName = this.getQueryFieldName(this.query, 'plan_year_no', '{replaceYear}', true);
        if (fieldName) {
            this.layerQuery.field = fieldName;
        }
    }

    getQueryFieldName(query: IQuery, fieldName: string, value: string, removeQuery = false) {
        if (query) {
            if (query.subqueries && query.subqueries.length && query.subqueries.length > 0) {
                for (let i = query.subqueries.length - 1; i >= 0; i--) {
                    const returnText = this.getQueryFieldName(query.subqueries[i], fieldName, value);
                    if (returnText) {
                        if (removeQuery && query.subqueries[i].field && query.subqueries[i].field.indexOf(fieldName) > -1 && query.subqueries[i].value.indexOf(value) > -1) {
                            query.subqueries[i] = null;
                            query.subqueries = query.subqueries.filter(x => x);
                        }
                        return returnText;
                    }
                }
            } else if (query.field && query.field.indexOf(fieldName) > -1 && query.value.indexOf(value) > -1) {
                const field = query.field.toString();
                return field;
            }
        }
        return null;
    }

    private replaceFields(extraFilter, layerFieldName, field, fieldToReplace) {
        if (this.layerQuery && this.layerQuery.field && this.layerQuery.field.toUpperCase() === layerFieldName) {
            this.layerQuery.field = extraFilter.field;
            this.layerQuery.value = extraFilter.value;
        } else if (this.layerQuery && this.layerQuery.field) {
            if (this.layerQuery.field.toLowerCase() === field) {
                this.layerQuery.field = fieldToReplace;
            }
        } else if (this.layerQuery && this.layerQuery.subqueries) {
            for (const q of this.layerQuery.subqueries) {
                if (q.field && q.field.toUpperCase() === layerFieldName) {
                    q.field = extraFilter.field;
                    q.value = extraFilter.value;
                } else if (q.field && q.field.toLowerCase() === field) {
                    q.field = fieldToReplace;
                }
            }
        }
    }

    valueChangeOperator(index: number) {
        if (this.workingSubqueries[index].operator) {
            // If operator is for multiple values, ensure selected values are in list of values
            // Else if operator includes NULL and a value is set, then save current value to possibly restore later since it is cleared
            // Else if operator does not include NULL and value is empty, then restore previous value
            if (this.filterService.showMultiple(this.workingSubqueries[index].operator) && !this.filterService.showMultiple(this.previousOperator)) {
                let selectedValues = [];

                if (this.workingSubqueries[index].multiValue) {
                    selectedValues = this.workingSubqueries[index].multiValue;
                }

                if (this.workingSubqueries[index].value) {
                    selectedValues = this.workingSubqueries[index].value.split(',').filter(v => v.trim() !== '');
                }

                // Go through all selected values
                selectedValues.forEach((value) => {
                    // If value not in list, then add custom value to lists
                    if (this.workingSubqueries[index].filteredDistinctValues.filter((distinctValue) => distinctValue.value === value).length === 0) {
                        this.workingSubqueries[index].customValues.push(value);
                        this.workingSubqueries[index].filteredDistinctValues.push({ text: value, value });
                    }
                });
            } else if (this.workingSubqueries[index].operator.toUpperCase().includes('NULL') && this.workingSubqueries[index].value) {
                this.previousValue[index] = this.workingSubqueries[index].value;
            } else if (!this.workingSubqueries[index].operator.toUpperCase().includes('NULL') && this.workingSubqueries[index].value === '') {
                this.workingSubqueries[index].value = this.previousValue[index];
                this.previousValue[index] = '';
            }

            this.previousOperator = this.workingSubqueries[index].operator;
        }

        this.changeDetectorRef.detectChanges();
        setTimeout(() => { this.refreshSubqueries(); }, shortWait);
    }

    valueChange() {
        this.changeDetectorRef.detectChanges();
        setTimeout(() => { this.refreshSubqueries(); }, shortWait);
    }

    onValueChange(value: any, index: number) {
        if (!this.workingSubqueries[index].customValues) {
            this.workingSubqueries[index].customValues = [];
        }

        let filteredDistinctValues = [];

        // If removing value
        // Otherwise, value selected
        if (!value) {
            // If value is a custom value
            if (this.workingSubqueries[index].customValues.length > 0) {
                // Go through all custom values
                this.workingSubqueries[index].customValues.forEach((customValue) => {
                    // Determine if custom value is in list
                    filteredDistinctValues = this.workingSubqueries[index].filteredDistinctValues.filter((distinctValue) => distinctValue.value === customValue);

                    // If custom value is in list, then remove it
                    if (filteredDistinctValues.length === 1) {
                        this.removeItemFromFilteredDistinctValues(index, customValue);
                    }

                });

                // Remove custom values (may be more than one if had multiple initially selected and then changed operator)
                this.workingSubqueries[index].customValues = [];
            }
        } else {
            // Determine if value is in list
            filteredDistinctValues = this.workingSubqueries[index].filteredDistinctValues.filter((distinctValue) => distinctValue.value === value);

            // If custom value not in list
            // Otherwise, not custom value
            if (filteredDistinctValues.length === 0) {
                // If custom value is not empty string
                if (value !== '') {
                    // If existing custom value, then remove
                    if (this.workingSubqueries[index].customValues.length === 1) {
                        this.removeItemFromFilteredDistinctValues(index, this.workingSubqueries[index].customValues[0]);
                    }

                    // Add custom value to lists
                    this.workingSubqueries[index].customValues = [value];
                    this.workingSubqueries[index].filteredDistinctValues.push({ text: value, value });
                }
            } else {
                // If there's a custom value, remove it
                if (this.workingSubqueries[index].customValues.length === 1) {
                    // Remove existing custom values (if exist)
                    this.removeItemFromFilteredDistinctValues(index, this.workingSubqueries[index].customValues[0]);

                    // Value not custom value so clear list
                    this.workingSubqueries[index].customValues = [];
                }
            }
        }

        this.changeDetectorRef.detectChanges();
        setTimeout(() => { this.refreshSubqueries(); }, shortWait);
    }

    onMultiValueChange(values: string[], index: number) {
        const checkCommaDelimited = !this.workingSubqueries[index].filteredDistinctValues
            || !this.workingSubqueries[index].filteredDistinctValues.every(dv => dv.value.includes(','));
        if (checkCommaDelimited) {
            values = this.workingSubqueries[index].multiValue = values.flatMap(value => {
                let retVal = [value];
                if (value.includes(',')) {
                    retVal = value.split(',').map(v => v.trim());
                }
                return retVal;
            });
        }

        if (!this.workingSubqueries[index].customValues) {
            this.workingSubqueries[index].customValues = [];
        }

        let isItemAdded;

        // Go through all selected values
        values.forEach((value) => {
            // If not empty string and value not in list, then new custom value
            if (value !== '' && this.workingSubqueries[index].filteredDistinctValues.filter((distinctValue) => distinctValue.value === value).length === 0) {
                // Add custom value to lists
                this.workingSubqueries[index].customValues.push(value);
                this.workingSubqueries[index].filteredDistinctValues.push({ text: value, value });

                isItemAdded = true;
            }
        });

        if (!isItemAdded) {
            // Go through custom values
            this.workingSubqueries[index].customValues.forEach((customValue, valueIndex) => {
                // If value not in list, then custom value removed
                if (values.filter((distinctValue) => distinctValue === customValue).length === 0) {
                    // Remove custom value from filtered list
                    this.removeItemFromFilteredDistinctValues(index, customValue);

                    // Remove custom value from custom value list
                    this.workingSubqueries[index].customValues.splice(valueIndex, 1);
                }
            });
        }

        this.changeDetectorRef.detectChanges();
        setTimeout(() => { this.refreshSubqueries(); }, shortWait);
    }

    removeItemFromFilteredDistinctValues(index: number, value: any) {
        this.workingSubqueries[index].filteredDistinctValues.forEach((item, itemIndex) => {
            if (item.value === value) {
                this.workingSubqueries[index].filteredDistinctValues.splice(itemIndex, 1);
            }
        });
    }

    valueNormalizer(text$: Observable<string>) {
        return text$.pipe(map((text: string) => {
            return {
                    value: text,
                    text
                };
        }));
    }

    resetBasicFilter() {
        this.query = {};
        this.workingSql = '';
        this.summaryItemsString = '';
        this.extraFilters = [];
        this.esriLayer = null;
    }

    resetLayerColumnsDisplayWork() {
        this.layerColumnsDisplay = this.layerColumns;
        this.sortLayerColumnsDisplay();
    }
    resetLayerColumnsDisplay = util.debounce(() => this.resetLayerColumnsDisplayWork(), shortWait);

    sortLayerColumnsDisplay() {
        this.layerColumnsDisplay.sort((a, b) => {
            const aSortText = a.alias.toLowerCase();
            const bSortText = b.alias.toLowerCase();
            return aSortText.localeCompare(bSortText);
        });
    }

    @Input()
    async setFilter(layerId: number, esriLayer: MapCoreLayer, layerColumns: ILayerColumn[], query: IQuery, externalLayerId?: string) {
        if (!externalLayerId) {
            this._layerId = layerId;
            this._query = query || {};
        } else {
            this._externalLayerId = externalLayerId;
            layerId = -1;
            this.query = query || {};
        }
        this.esriLayer = esriLayer;
        this.layerColumns = layerColumns || [];
        await this.setLayer(layerId);
    }

    selectedColumn(column: string) {
        return this.layerColumnsDisplay.find(x => x.name === column);
    }
}
